/**
 * Public URL slugs — the client half of internal/service/seo/slug.go.
 *
 * A shareable link is `{name}-{id8}`: a readable name segment plus the first
 * eight hex characters of the row's UUID. The id is what resolves; the name is
 * what the reader sees, so a kitchen that renames itself keeps every link ever
 * shared and the server redirects the old spelling to the new one.
 *
 * This file exists because the app has to be able to BUILD those URLs — a card
 * needs an href, a share button needs a link to copy — while the server owns
 * resolving them. The two implementations must agree; `slug.test.ts` pins them
 * to the same cases the Go tests use.
 */

const DIACRITICS: Record<string, string> = {
  à: 'a', á: 'a', â: 'a', ã: 'a', ä: 'a', å: 'a', ā: 'a', ă: 'a', ą: 'a',
  è: 'e', é: 'e', ê: 'e', ë: 'e', ē: 'e', ĕ: 'e', ė: 'e', ę: 'e', ě: 'e', ẹ: 'e',
  ì: 'i', í: 'i', î: 'i', ï: 'i', ī: 'i', į: 'i',
  ò: 'o', ó: 'o', ô: 'o', õ: 'o', ö: 'o', ō: 'o', ŏ: 'o', ő: 'o', ọ: 'o',
  ù: 'u', ú: 'u', û: 'u', ü: 'u', ū: 'u', ŭ: 'u', ů: 'u', ű: 'u', ų: 'u',
  ñ: 'n', ń: 'n', ņ: 'n', ň: 'n',
  ç: 'c', ć: 'c', ĉ: 'c', ċ: 'c', č: 'c',
  ş: 's', ś: 's', ŝ: 's', ș: 's', ṣ: 's',
  ý: 'y', ÿ: 'y',
  ğ: 'g', ĝ: 'g', ġ: 'g', ģ: 'g',
  ł: 'l',
  ź: 'z', ż: 'z', ž: 'z',
  æ: 'ae', œ: 'oe', ß: 'ss',
}

const ID_SEGMENT = /^[0-9a-f]{8}$/

/**
 * Reduce a display name to URL-safe lowercase words.
 *
 * Diacritics are folded rather than dropped, so "Adédèjì" becomes "adedeji" and
 * not "ADJ" — Nigerian kitchen names carry tone marks, and a slug that deletes
 * them is unreadable. An apostrophe joins rather than separates, so "Amara's
 * Kitchen" is two words and not three.
 */
export function slugify(name: string): string {
  const lowered = (name ?? '').trim().toLowerCase()
  let out = ''
  // Decompose first so a name stored as "e" + combining dot resolves to the
  // same slug as one stored as the precomposed character. The combining marks
  // left behind are stripped by the class below.
  for (const ch of lowered.normalize('NFD')) {
    if (ch >= '̀' && ch <= 'ͯ') continue
    if (ch === '’' || ch === "'") continue
    const folded = DIACRITICS[ch]
    if (folded !== undefined) {
      out += folded
      continue
    }
    out += ch.charCodeAt(0) > 127 ? '-' : ch
  }
  return out.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

/** The resolving half of a slug: the first eight hex characters of a UUID. */
export function shortId(id: string): string {
  const head = (id ?? '').trim().toLowerCase().slice(0, 8)
  return ID_SEGMENT.test(head) ? head : ''
}

/** The one spelling of a row's URL segment. */
export function canonicalSlug(name: string, id: string): string {
  const short = shortId(id)
  const base = slugify(name)
  if (!short) return base
  if (!base) return short
  return `${base}-${short}`
}

/** The public path of a kitchen's page, from its discovery projection. */
export const kitchenPath = (chef: { id: string; name: string }): string =>
  `/kitchen/${canonicalSlug(chef.name, chef.id)}`

/** The public path of a dish's page, from its discovery projection. */
export const dishPath = (dish: { id: string; name: string }): string =>
  `/dish/${canonicalSlug(dish.name, dish.id)}`
